/**
 * Financial Translation Module - Staff ROI.
 * Ported from Python financial_translator.py
 *
 * Converts brain health scores into business ROI metrics.
 * Channels: Stress, Accuracy, Productivity, Retention.
 *
 * Evidence-backed per-pathway coefficients from peer-reviewed research.
 */

// ── Per-pathway effect sizes from published research ──
export const PATHWAY_COEFFICIENTS = {
  daylight:       { channel: 'accuracy',     effect: 0.03, study: 'Boubekri 2020', desc: '+3% productivity from daylight access' },
  noise:          { channel: 'stress',       effect: 0.05, study: 'Banbury & Berry 2005', desc: '-5% performance from noise distraction' },
  air_quality:    { channel: 'accuracy',     effect: 0.08, study: 'Allen et al. 2015 COGfx', desc: '+8% cognitive function with better air' },
  nature:         { channel: 'retention',    effect: 0.06, study: 'Bjornstad 2016', desc: '-6% absenteeism from nature access' },
  enrichment:     { channel: 'accuracy',     effect: 0.06, study: 'Nieuwenhuis 2014', desc: '+6% productivity in enriched offices' },
  movement:       { channel: 'productivity', effect: 0.02, study: 'Conn 2009 meta-analysis', desc: '+2% productivity from workplace activity' },
  wayfinding:     { channel: 'productivity', effect: 0.01, study: 'Jamshidi 2025', desc: '+1% FTE recovery from better navigation' },
  visual_stress:  { channel: 'stress',       effect: 0.02, study: 'Wilkins 1989', desc: '-2% absenteeism from visual comfort' },
};

// ── Baseline rates for channel calculations ──
const BASELINE_RATES = {
  absenteeismRate: 0.038,     // BLS average
  presenteeismRate: 0.15,     // Hemp 2004; conservative
  reworkRate: 0.05,           // Industry benchmark
  keyWorkFraction: 0.60,      // Fraction of hours on core deliverables
  turnoverRate: 0.15,         // National average
};

// ── Role-type defaults (from HumanSpace model) ──
export const ROLE_DEFAULTS = {
  Executive: {
    label: 'Executive / Director',
    avgHourlyCost: 95,
    replacementCostMultiple: 2.0,
    annualTurnoverRate: 0.08,
    pctTimeRework: 0.03,
    pctTimeKeyWork: 0.70,
  },
  Manager: {
    label: 'Manager',
    avgHourlyCost: 55,
    replacementCostMultiple: 1.5,
    annualTurnoverRate: 0.12,
    pctTimeRework: 0.05,
    pctTimeKeyWork: 0.60,
  },
  Technical: {
    label: 'Technical / Clinical',
    avgHourlyCost: 45,
    replacementCostMultiple: 1.2,
    annualTurnoverRate: 0.18,
    pctTimeRework: 0.06,
    pctTimeKeyWork: 0.65,
  },
  Administrative: {
    label: 'Administrative / Support',
    avgHourlyCost: 25,
    replacementCostMultiple: 0.5,
    annualTurnoverRate: 0.25,
    pctTimeRework: 0.05,
    pctTimeKeyWork: 0.55,
  },
};

// ── Evidence Citations ──
export const STAFF_EVIDENCE = {
  stress: [
    { author: 'Banbury & Berry', year: 2005, finding: 'Office noise reduces performance by ~5%; irrelevant speech most disruptive', channel: 'Stress' },
    { author: 'Wilkins', year: 1989, finding: 'Visual pattern stress (fluorescent flicker, striped patterns) increases headache/absenteeism by ~2%', channel: 'Stress' },
  ],
  accuracy: [
    { author: 'Boubekri et al.', year: 2020, finding: 'Workers with daylight access show +3% higher cognitive performance scores', channel: 'Accuracy' },
    { author: 'Allen et al.', year: 2015, finding: 'COGfx study: +8% cognitive function in green/well-ventilated buildings', channel: 'Accuracy' },
    { author: 'Nieuwenhuis et al.', year: 2014, finding: 'Enriched (plants, art) offices improve productivity by 6% vs lean offices', channel: 'Accuracy' },
  ],
  productivity: [
    { author: 'Conn et al.', year: 2009, finding: 'Meta-analysis: workplace physical activity programs improve work ability by ~2%', channel: 'Productivity' },
    { author: 'Jamshidi et al.', year: 2025, finding: 'Better wayfinding recovers ~1% of FTE time lost to navigation in large facilities', channel: 'Productivity' },
  ],
  retention: [
    { author: 'Bjornstad et al.', year: 2016, finding: 'Nature/greenery access reduces absenteeism by 6% and improves reported wellbeing', channel: 'Retention' },
  ],
};

// ── Channel scoring with per-pathway coefficients ──

function stressChannelValue(pathwayScores, totalAnnualPayroll) {
  // Noise and visual stress reduce performance → absenteeism + presenteeism savings
  const noiseRaw = Math.abs(pathwayScores.noise || 0); // 0 = worst, 1 = best (inverted)
  const visualRaw = Math.abs(pathwayScores.visual_stress || 0);

  // Improvement = how much better than worst case (higher = more improvement)
  const noiseImprovement = 1.0 - noiseRaw;
  const visualImprovement = 1.0 - visualRaw;

  const noiseEffect = PATHWAY_COEFFICIENTS.noise.effect;
  const visualEffect = PATHWAY_COEFFICIENTS.visual_stress.effect;

  // Savings from reducing absenteeism and presenteeism
  const noiseSaving = noiseImprovement * noiseEffect * totalAnnualPayroll;
  const visualSaving = visualImprovement * visualEffect * totalAnnualPayroll;

  const totalSaving = noiseSaving + visualSaving;
  const pct = totalAnnualPayroll > 0 ? totalSaving / totalAnnualPayroll : 0;

  return { value: totalSaving, pct };
}

function accuracyChannelValue(pathwayScores, totalReworkCost) {
  // Daylight, air quality, enrichment improve cognitive accuracy → reduce rework
  const daylight = Math.max(0, pathwayScores.daylight || 0);
  const air = Math.max(0, 1.0 + (pathwayScores.air_quality || 0));
  const enrichment = Math.max(0, pathwayScores.enrichment || 0);

  const daylightEffect = PATHWAY_COEFFICIENTS.daylight.effect;
  const airEffect = PATHWAY_COEFFICIENTS.air_quality.effect;
  const enrichmentEffect = PATHWAY_COEFFICIENTS.enrichment.effect;

  const saving = (daylight * daylightEffect + air * airEffect + enrichment * enrichmentEffect) * totalReworkCost;
  const pct = totalReworkCost > 0 ? saving / totalReworkCost : 0;

  return { value: saving, pct };
}

function productivityChannelValue(pathwayScores, totalKeyWorkCost) {
  // Movement and wayfinding improve throughput
  const movement = Math.max(0, pathwayScores.movement || 0);
  const wayfinding = Math.max(0, pathwayScores.wayfinding || 0);

  const movementEffect = PATHWAY_COEFFICIENTS.movement.effect;
  const wayfindingEffect = PATHWAY_COEFFICIENTS.wayfinding.effect;

  const saving = (movement * movementEffect + wayfinding * wayfindingEffect) * totalKeyWorkCost;
  const pct = totalKeyWorkCost > 0 ? saving / totalKeyWorkCost : 0;

  return { value: saving, pct };
}

function retentionChannelValue(pathwayScores, annualTurnoverCost) {
  // Nature access improves wellbeing → reduces turnover
  const nature = Math.max(0, pathwayScores.nature || 0);
  const natureEffect = PATHWAY_COEFFICIENTS.nature.effect;

  const saving = nature * natureEffect * annualTurnoverCost;
  const pct = annualTurnoverCost > 0 ? saving / annualTurnoverCost : 0;

  return { value: saving, pct };
}

/**
 * Calculate staff financial ROI from brain health assessment.
 * @param {number} brainHealthScore - Overall score 0-1
 * @param {Object} pathwayScores - Dict of 8 pathway scores
 * @param {Array|Object} peopleData - Array of role-level data OR single object (normalized internally)
 * @param {number} projectCost - Annual facility/renovation cost
 * @returns {Object} Financial result with channels array
 */
export function calculateStaffRoi(brainHealthScore, pathwayScores, peopleData, projectCost = 0) {
  // Normalize: accept single object or array
  const people = Array.isArray(peopleData) ? peopleData : [peopleData];

  let totalAnnualPayroll = 0;
  let totalHeadcount = 0;
  let totalReplacementPool = 0;
  let totalReworkHoursCost = 0;
  let totalKeyWorkCost = 0;
  let weightedTurnoverRate = 0;

  for (const person of people) {
    const hc = Number(person.headcount) || 0;
    const hourly = Number(person.avgHourlyCost || person.avg_hourly_cost) || 0;
    const hoursPerWeek = Number(person.hoursPerWeek || person.hours_per_week) || 40;
    const annualHours = hoursPerWeek * 47; // ~47 working weeks
    const replMult = Number(person.replacementCostMultiple || person.avg_replacement_cost_pct) || 1.5;
    const reworkPct = Number(person.pctTimeRework || person.pct_time_rework) || BASELINE_RATES.reworkRate;
    const keyPct = Number(person.pctTimeKeyWork || person.pct_time_key_work) || BASELINE_RATES.keyWorkFraction;
    const turnover = Number(person.annualTurnoverRate || person.annual_turnover_rate) || BASELINE_RATES.turnoverRate;

    const annual = hc * hourly * annualHours;
    totalAnnualPayroll += annual;
    totalHeadcount += hc;
    totalReplacementPool += hc * hourly * annualHours * replMult;
    totalReworkHoursCost += annual * reworkPct;
    totalKeyWorkCost += annual * keyPct;
    weightedTurnoverRate += turnover * hc;
  }

  const emptyResult = {
    overallBrainHealthScore: brainHealthScore,
    channels: [
      { name: 'Stress Reduction', annualSavings: 0, pct: 0 },
      { name: 'Accuracy / Rework', annualSavings: 0, pct: 0 },
      { name: 'Productivity', annualSavings: 0, pct: 0 },
      { name: 'Retention', annualSavings: 0, pct: 0 },
    ],
    totalAnnualImpact: 0,
    totalAnnualBenefit: 0,
    totalAnnualSavings: 0,
    paybackPct: 0,
    paybackYears: null,
    roi: null,
  };

  if (totalHeadcount === 0) return emptyResult;

  const avgTurnover = weightedTurnoverRate / totalHeadcount;
  const avgReplacement = totalReplacementPool / totalHeadcount;
  const annualTurnoverCost = totalHeadcount * avgTurnover * avgReplacement;

  // Calculate channels
  const stress = stressChannelValue(pathwayScores, totalAnnualPayroll);
  const accuracy = accuracyChannelValue(pathwayScores, totalReworkHoursCost);
  const productivity = productivityChannelValue(pathwayScores, totalKeyWorkCost);
  const retention = retentionChannelValue(pathwayScores, annualTurnoverCost);

  const totalAnnual = stress.value + accuracy.value + productivity.value + retention.value;
  const paybackPct = projectCost > 0 ? (totalAnnual / projectCost * 100) : 0;
  const paybackYears = totalAnnual > 0 ? (projectCost / totalAnnual) : null;
  const roi = projectCost > 0 ? totalAnnual / projectCost : null;

  const channels = [
    { name: 'Stress Reduction', annualSavings: Math.round(stress.value * 100) / 100, pct: Math.round(stress.pct * 10000) / 10000 },
    { name: 'Accuracy / Rework', annualSavings: Math.round(accuracy.value * 100) / 100, pct: Math.round(accuracy.pct * 10000) / 10000 },
    { name: 'Productivity', annualSavings: Math.round(productivity.value * 100) / 100, pct: Math.round(productivity.pct * 10000) / 10000 },
    { name: 'Retention', annualSavings: Math.round(retention.value * 100) / 100, pct: Math.round(retention.pct * 10000) / 10000 },
  ];

  return {
    overallBrainHealthScore: Math.round(brainHealthScore * 10000) / 10000,
    channels,
    // Legacy fields
    stressImpactPct: channels[0].pct,
    stressAnnualValue: channels[0].annualSavings,
    accuracyImpactPct: channels[1].pct,
    accuracyAnnualValue: channels[1].annualSavings,
    productivityImpactPct: channels[2].pct,
    productivityAnnualValue: channels[2].annualSavings,
    retentionImpactPct: channels[3].pct,
    retentionAnnualValue: channels[3].annualSavings,
    // Totals with aliases
    totalAnnualImpact: Math.round(totalAnnual * 100) / 100,
    totalAnnualBenefit: Math.round(totalAnnual * 100) / 100,
    totalAnnualSavings: Math.round(totalAnnual * 100) / 100,
    paybackPct: Math.round(paybackPct * 100) / 100,
    paybackYears: paybackYears !== null ? Math.round(paybackYears * 100) / 100 : null,
    roi: roi !== null ? Math.round(roi * 10000) / 10000 : null,
  };
}
